/*
 * promo-code.js
 *
 * Generates a random alphanumeric promo code on page load, optionally
 * registers it with a webhook (settings.promo_code_webhook_url) for
 * recording / Excel export, and renders it inside any
 * <promo-code-display> custom element on the page.
 *
 * Local de-duplication:
 *   Recent codes are stored in localStorage under `promoCode:recent:<prefix>`
 *   with a TTL equal to the configured "uniqueness window" (default 14 days).
 *   We try up to `localMaxAttempts` times to avoid re-using a recent one.
 *
 * Server de-duplication:
 *   If a webhook is configured and `strict` mode is on, the displayed code
 *   is only committed once the webhook returns `{ ok: true }`. If the
 *   webhook returns `{ ok: false, suggestedCode: "..." }` we use the
 *   suggested code instead. Up to `serverMaxAttempts` retries with a fresh
 *   candidate are performed before falling back to a local-only code.
 */
(function () {
  'use strict';

  const CHARSETS = {
    alnum: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
    alpha: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    digits: '0123456789',
    no_confuse: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789', // drops 0/O/1/I/L
  };

  function randomChar(charset) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return charset.charAt(arr[0] % charset.length);
  }

  function generateRandomPart(length, charset) {
    let out = '';
    for (let i = 0; i < length; i++) out += randomChar(charset);
    return out;
  }

  function getRecentStore(prefix, ttlMs) {
    const key = 'promoCode:recent:' + prefix;
    let raw = [];
    try {
      raw = JSON.parse(localStorage.getItem(key) || '[]');
    } catch (e) {
      raw = [];
    }
    const cutoff = Date.now() - ttlMs;
    const fresh = raw.filter((entry) => entry && entry.t >= cutoff);
    if (fresh.length !== raw.length) {
      try {
        localStorage.setItem(key, JSON.stringify(fresh));
      } catch (e) {
        /* ignore quota */
      }
    }
    const set = new Set(fresh.map((e) => e.c));
    return {
      has: (code) => set.has(code),
      remember: (code) => {
        fresh.push({ c: code, t: Date.now() });
        set.add(code);
        try {
          localStorage.setItem(key, JSON.stringify(fresh.slice(-2000)));
        } catch (e) {
          /* ignore quota */
        }
      },
    };
  }

  function buildCandidate(cfg) {
    const charset = CHARSETS[cfg.charset] || CHARSETS.alnum;
    return cfg.prefix + generateRandomPart(cfg.length, charset);
  }

  async function postToWebhook(url, payload, secret) {
    /*
     * IMPORTANT: We deliberately send the body as text/plain.
     *
     * Most webhook backends (notably Google Apps Script `/exec` endpoints)
     * cannot satisfy a CORS preflight (OPTIONS) response. Using
     * Content-Type: application/json triggers that preflight and the
     * request is silently blocked by the browser.
     *
     * text/plain is a "simple request" — no preflight, the browser POSTs
     * directly. Apps Script still receives the raw JSON body via
     * e.postData.contents and parses it with JSON.parse().
     */
    let finalUrl = url;
    if (secret) {
      // Apps Script strips most custom headers, so also append the secret
      // as a query param as a fallback. Server can check either.
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      finalUrl = url + sep + 'secret=' + encodeURIComponent(secret);
    }
    const res = await fetch(finalUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      keepalive: true,
      mode: 'cors',
      credentials: 'omit',
      redirect: 'follow',
    });
    if (!res.ok) throw new Error('Webhook HTTP ' + res.status);
    const text = await res.text();
    if (!text) return { ok: true };
    try {
      return JSON.parse(text);
    } catch (e) {
      return { ok: true };
    }
  }

  function getOrCreateSessionId() {
    try {
      let sid = sessionStorage.getItem('promoCodeSessionId');
      if (!sid) {
        sid = 's_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        sessionStorage.setItem('promoCodeSessionId', sid);
      }
      return sid;
    } catch (e) {
      return 's_' + Math.random().toString(36).slice(2, 10);
    }
  }

  class PromoCodeDisplay extends HTMLElement {
    constructor() {
      super();
      this.codeEl = null;
    }

    connectedCallback() {
      this.codeEl = this.querySelector('.promo-code-display') || this;
      this.codeEl.dataset.state = 'loading';
      this.codeEl.textContent = this.dataset.placeholder || '----';
      this.generate();
    }

    config() {
      const ds = this.dataset;
      return {
        prefix: ds.prefix || '',
        length: Math.max(1, Math.min(12, parseInt(ds.length, 10) || 4)),
        charset: ds.charset || 'alnum',
        uniqueDays: Math.max(0, parseInt(ds.uniqueDays, 10) || 14),
        webhook: ds.webhook || '',
        secret: ds.secret || '',
        strict: ds.strict === 'true',
        localMaxAttempts: 25,
        serverMaxAttempts: 5,
        pageHandle: ds.pageHandle || '',
        templateId: ds.templateId || '',
      };
    }

    async generate() {
      const cfg = this.config();
      const ttlMs = cfg.uniqueDays * 24 * 60 * 60 * 1000;
      const store = ttlMs > 0 ? getRecentStore(cfg.prefix || '_', ttlMs) : null;

      // 1) Build a local candidate that's not in our local "recent" set.
      let candidate = buildCandidate(cfg);
      if (store) {
        let attempt = 0;
        while (store.has(candidate) && attempt < cfg.localMaxAttempts) {
          candidate = buildCandidate(cfg);
          attempt++;
        }
      }

      // 2) Show it immediately — no loading state, no spinner.
      //    If the webhook later returns a different code we'll swap it in.
      this.commitCode(candidate, store);

      // 3) Webhook call runs in the background.
      if (!cfg.webhook) {
        console.info(
          '[promo-code] No webhook URL configured. Codes are generated locally only. ' +
            'Set one in Theme Settings > Promotional Pages > Promo code webhook URL.'
        );
        return;
      }
      console.debug('[promo-code] POST', cfg.webhook, '→ candidate', candidate);

      let finalCode = candidate;
      let serverAttempts = 0;

      while (serverAttempts < cfg.serverMaxAttempts) {
        try {
          const payload = {
            code: finalCode,
            prefix: cfg.prefix,
            page_handle: cfg.pageHandle,
            template: cfg.templateId,
            generated_at: new Date().toISOString(),
            session_id: getOrCreateSessionId(),
            user_agent: navigator.userAgent,
            referrer: document.referrer || '',
          };
          const resp = await postToWebhook(cfg.webhook, payload, cfg.secret);
          console.debug('[promo-code] webhook response', resp);
          if (resp && resp.ok === false && resp.suggestedCode) {
            finalCode = String(resp.suggestedCode).toUpperCase();
            this.commitCode(finalCode, store);
            serverAttempts++;
            continue;
          }
          if (resp && resp.ok === false) {
            finalCode = buildCandidate(cfg);
            this.commitCode(finalCode, store);
            serverAttempts++;
            continue;
          }
          return;
        } catch (err) {
          console.error(
            '[promo-code] webhook POST failed. Common causes:\n' +
              ' 1) Apps Script endpoint requires text/plain (we already send that).\n' +
              ' 2) Apps Script deployment is not set to "Anyone" access — redeploy as a new version with "Who has access: Anyone".\n' +
              ' 3) URL is wrong / not the /exec URL.\n' +
              ' 4) The dev theme has no value saved for promo_code_webhook_url (set it in Theme Settings or settings_data.json).',
            err
          );
          return;
        }
      }
    }

    commitCode(code, store, state) {
      if (store) store.remember(code);
      this.codeEl.textContent = code;
      this.codeEl.dataset.state = state || 'ready';
      this.codeEl.setAttribute('aria-label', 'Discount code ' + code);
      this.dispatchEvent(
        new CustomEvent('promo-code:ready', { detail: { code }, bubbles: true })
      );
    }
  }

  if (!customElements.get('promo-code-display')) {
    customElements.define('promo-code-display', PromoCodeDisplay);
  }
})();
